import { ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  ProjectPermissionEntryDTO,
  UserProjectPermissionsDTO,
  effectivePermissions,
  samePermissions,
} from '../../models/user-project-permissions.model';

export type EinzelrechteMatrixState = 'loading' | 'error' | 'ready' | 'invitation' | 'forbidden';

interface SectionDef {
  id: string;
  icon: string;
  permissions: readonly string[];
}

const SECTIONS: readonly SectionDef[] = [
  {
    id: 'testcases',
    icon: 'check_circle',
    permissions: ['TESTCASE_READ', 'TESTCASE_WRITE', 'TESTMODULE_READ', 'TESTMODULE_WRITE'],
  },
  { id: 'requirements', icon: 'edit', permissions: ['REQUIREMENT_READ', 'REQUIREMENT_WRITE', 'REQUIREMENT_LINK'] },
  { id: 'testplans', icon: 'event_note', permissions: ['TESTPLAN_READ', 'TESTPLAN_WRITE'] },
  {
    id: 'project',
    icon: 'folder',
    permissions: ['PROJECT_READ', 'PROJECT_WRITE', 'RELEASE_MANAGE', 'ENVIRONMENT_MANAGE', 'CATALOG_MANAGE'],
  },
];

let nextMatrixId = 0;

const OTHER_SECTION_ID = 'other';
const KNOWN_PERMISSIONS = new Set(SECTIONS.flatMap((section) => section.permissions));

const ADDED_KEY = 'userEdit.berechtigungen.added';
const REVOKED_KEY = 'userEdit.berechtigungen.revoked';

const PROJECT_ROLE_KEYS: Readonly<Record<string, string>> = {
  PROJECT_ADMIN: 'projectForm.team.roles.lead',
  PROJECT_WRITER: 'projectForm.team.roles.tester',
  PROJECT_READER: 'projectForm.team.roles.observer',
};

interface MatrixRow {
  permission: string;
  labelKey: string | null;
  checked: boolean;
  locked: boolean;
  badgeKey: string | null;
}

interface MatrixSection {
  id: string;
  icon: string;
  labelKey: string;
  rows: MatrixRow[];
}

@Component({
  selector: 'app-einzelrechte-matrix',
  imports: [TranslatePipe, MatIconModule, MatTooltipModule, MatFormFieldModule, MatSelectModule, MatCheckboxModule],
  templateUrl: './einzelrechte-matrix.component.html',
  styleUrl: './einzelrechte-matrix.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EinzelrechteMatrixComponent {
  readonly data = input<UserProjectPermissionsDTO | null>(null);
  readonly state = input<EinzelrechteMatrixState>('loading');
  readonly userName = input('');
  readonly tenantRoleLabel = input('');
  readonly disabled = input(false);
  readonly selectedProjectId = model<string | null>(null);
  readonly checked = model<Record<string, string[]>>({});

  readonly revokedKey = REVOKED_KEY;
  readonly badgeIdPrefix = `einzelrechte-matrix-${nextMatrixId++}-badge-`;

  readonly projects = computed(() => this.data()?.projects ?? []);
  readonly editable = computed(() => this.data()?.editable === true);
  readonly readOnlyReason = computed(() => this.data()?.readOnlyReason ?? null);

  readonly selectedEntry = computed<ProjectPermissionEntryDTO | null>(() => {
    const projects = this.projects();
    return projects.find((project) => project.projectId === this.selectedProjectId()) ?? projects[0] ?? null;
  });

  private readonly currentChecked = computed<string[]>(() => {
    const entry = this.selectedEntry();
    if (!entry) return [];
    return this.checked()[entry.projectId] ?? effectivePermissions(entry);
  });

  readonly roleLabelKeys = computed(() =>
    (this.selectedEntry()?.roles ?? []).map((role) => PROJECT_ROLE_KEYS[role] ?? role),
  );

  readonly canReset = computed(() => {
    const entry = this.selectedEntry();
    return !!entry && this.editable() && !samePermissions(this.currentChecked(), entry.rolePermissions);
  });

  readonly sections = computed<MatrixSection[]>(() => {
    const data = this.data();
    const entry = this.selectedEntry();
    if (!data || !entry) return [];
    const catalog = data.catalog;
    const known: MatrixSection[] = SECTIONS.map((section) => ({
      id: section.id,
      icon: section.icon,
      labelKey: `userEdit.berechtigungen.sections.${section.id}`,
      rows: section.permissions
        .filter((permission) => catalog.includes(permission))
        .map((permission) => this.row(data, entry, permission, `userEdit.berechtigungen.perms.${permission}`)),
    }));
    const other: MatrixSection = {
      id: OTHER_SECTION_ID,
      icon: 'more_horiz',
      labelKey: `userEdit.berechtigungen.sections.${OTHER_SECTION_ID}`,
      rows: catalog
        .filter((permission) => !KNOWN_PERMISSIONS.has(permission))
        .map((permission) => this.row(data, entry, permission, null)),
    };
    return [...known, other].filter((section) => section.rows.length > 0);
  });

  selectProject(projectId: string): void {
    this.selectedProjectId.set(projectId);
  }

  toggle(permission: string, value: boolean): void {
    const entry = this.selectedEntry();
    const catalog = this.data()?.catalog ?? [];
    if (!entry || !this.editable() || this.disabled()) return;
    const current = this.currentChecked();
    const next = catalog.filter((name) => (name === permission ? value : current.includes(name)));
    this.checked.update((all) => ({ ...all, [entry.projectId]: next }));
  }

  resetToRole(): void {
    const entry = this.selectedEntry();
    const catalog = this.data()?.catalog ?? [];
    if (!entry || this.disabled()) return;
    const next = catalog.filter((name) => entry.rolePermissions.includes(name));
    this.checked.update((all) => ({ ...all, [entry.projectId]: next }));
  }

  private row(
    data: UserProjectPermissionsDTO,
    entry: ProjectPermissionEntryDTO,
    permission: string,
    labelKey: string | null,
  ): MatrixRow {
    const checked = this.currentChecked().includes(permission);
    const fromRole = entry.rolePermissions.includes(permission);
    let badgeKey: string | null = null;
    if (checked && !fromRole) badgeKey = ADDED_KEY;
    else if (!checked && fromRole) badgeKey = REVOKED_KEY;
    return {
      permission,
      labelKey,
      checked,
      locked: checked && fromRole && !data.revocable.includes(permission),
      badgeKey,
    };
  }
}
